import type { ExtractedStatement, RenderableStatementReference } from './extractedStatements';

import type { PublicationShareEntry } from '@/types/messages/base';

type PendingStatementReference = Extract<RenderableStatementReference, { kind: 'pending' }>;

/**
 * A reference the author described and Primum staff have not yet found,
 * numbered after the server's references. `statementType` is copied from the
 * parent statement so analytics call sites can emit it without re-threading
 * the full `extractedStatements` array.
 */
export interface NumberedPendingReference {
  number: number;
  statementIndex: number;
  statementType: string;
  reference: PendingStatementReference;
}

/**
 * The numbers a message's statements carry.
 *
 * - `byStatement` maps a statement's index to the numbers drawn as `[N]`
 *   chips after its underlined run, ascending.
 * - `pending` lists the author's described-but-unfound references, for the
 *   footer and sheet rows that say Primum is finding them.
 * - `footnoteCount` counts every chip across the statements, a work on two
 *   statements twice, as `Reference viewed` has always reported it.
 */
export interface ReferenceNumbering {
  byStatement: Map<number, number[]>;
  pending: NumberedPendingReference[];
  footnoteCount: number;
}

/**
 * Statement chip numbers, taken from the server's numbering in the message's
 * `publicationShares` entries: each entry's number goes on every statement it
 * is attached to, so a paper attached to two statements reads `[1]` on both.
 * Attached URLs are not numbered here: every resolved attached reference is a
 * share, and the server has already numbered it with everything else the
 * message cites, in reading order.
 *
 * Pending references are not shares, so they are numbered here, after the
 * server's last number, in statement order. Server numbers never change for
 * them, and the numbers the reader can open run 1 to N with no gaps. When
 * Primum finds one, it becomes a share and takes the number the server gives
 * it. `mergeStatementReferences` has already removed them for the recipient.
 *
 * An index outside the statements is ignored, since there is no underlined
 * run to put its chip after.
 */
export function computeReferenceNumbering(
  extractedStatements: readonly ExtractedStatement[] | undefined,
  references: readonly RenderableStatementReference[] | undefined,
  entries: readonly PublicationShareEntry[] = []
): ReferenceNumbering {
  const byStatement = new Map<number, number[]>();

  const pending: NumberedPendingReference[] = [];

  const statementCount = extractedStatements?.length ?? 0;

  let nextPendingNumber =
    entries.reduce((highest, entry) => Math.max(highest, entry.number), 0) + 1;

  for (let statementIndex = 0; statementIndex < statementCount; statementIndex += 1) {
    const sharedNumbers = entries
      .filter((entry) => entry.statementIndices.includes(statementIndex))
      .map((entry) => entry.number)
      .sort((first, second) => first - second);

    const pendingForStatement = (references ?? []).filter(
      (entry): entry is PendingStatementReference =>
        entry.kind === 'pending' && entry.statementIndex === statementIndex
    );

    const statementType = extractedStatements?.[statementIndex]?.statementType ?? '';

    const pendingNumbers = pendingForStatement.map((reference) => {
      const number = nextPendingNumber;

      nextPendingNumber += 1;

      pending.push({ number, statementIndex, statementType, reference });

      return number;
    });

    const numbers = [...new Set(sharedNumbers), ...pendingNumbers];

    if (numbers.length > 0) {
      byStatement.set(statementIndex, numbers);
    }
  }

  const footnoteCount = [...byStatement.values()].reduce(
    (total, numbers) => total + numbers.length,
    0
  );

  return { byStatement, pending, footnoteCount };
}
